import { createTheme } from "@material-ui/core/styles";

const theme = createTheme({
  palette: {
    primary: {
      main: '#007C5A',
      dark: '#005f44',
      light: '#39ac7d',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#ffeb00',
      dark: '#d9c800',
      light: '#ffef42',
      contrastText: '#1a2e22',
    },
    background: {
      default: '#eef3ef',
      paper: '#ffffff',
    },
    text: {
      primary: '#1a2e22',
      secondary: '#4f6b5c',
    },
    divider: 'rgba(0,0,0,0.08)',
    grey: {
      50: '#f8faf9',
      100: '#eef3ef',
      200: '#dde8e3',
      300: '#c5d7cd',
      400: '#9ab5a7',
      500: '#6f9280',
      600: '#4e6e5b',
    },
    error: { main: '#e53935' },
    warning: { main: '#f59e0b' },
    success: { main: '#10b981' },
    info: { main: '#3b82f6' },
  },
  typography: {
    fontFamily: "'Montserrat', 'Roboto', 'Helvetica Neue', sans-serif",
    h1: {
      fontSize: '1.5rem',
      fontWeight: 700,
      letterSpacing: '-0.025em',
      lineHeight: 1.2,
      color: '#1a2e22',
    },
    h2: {
      fontSize: '1.25rem',
      fontWeight: 600,
      letterSpacing: '-0.015em',
      color: '#1a2e22',
    },
    h3: {
      fontSize: '1.1rem',
      fontWeight: 600,
      color: '#1a2e22',
    },
    h4: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#1a2e22',
    },
    h5: {
      fontSize: '0.9rem',
      fontWeight: 500,
      color: '#4f6b5c',
    },
    h6: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#4f6b5c',
    },
    body1: {
      fontSize: '0.875rem',
      lineHeight: 1.65,
    },
    body2: {
      fontSize: '0.8125rem',
      lineHeight: 1.5,
    },
    subtitle1: {
      fontSize: '0.875rem',
      fontWeight: 500,
    },
    subtitle2: {
      fontSize: '0.8125rem',
      fontWeight: 500,
      letterSpacing: '-0.01em',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.875rem',
      letterSpacing: '0',
    },
    caption: {
      fontSize: '0.75rem',
      color: '#4f6b5c',
    },
    overline: {
      fontSize: '0.7rem',
      fontWeight: 700,
      letterSpacing: '0.1em',
    },
  },
  shape: {
    borderRadius: 8,
  },
  overrides: {
    MuiCssBaseline: {
      '@global': {
        body: {
          backgroundColor: '#eef3ef',
        },
        '*': {
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(0,0,0,0.2) transparent',
        },
        '*::-webkit-scrollbar': {
          width: '6px',
          height: '6px',
        },
        '*::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.18)',
          borderRadius: '10px',
        },
        '*::-webkit-scrollbar-thumb:hover': {
          backgroundColor: 'rgba(0,0,0,0.35)',
        },
      },
    },
    MuiButton: {
      root: {
        borderRadius: 8,
        padding: '8px 20px',
        transition: 'all 0.2s ease',
        fontWeight: 600,
        lineHeight: 1.5,
      },
      contained: {
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        '&:hover': {
          boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
          transform: 'translateY(-1px)',
        },
        '&:active': {
          transform: 'translateY(0)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        },
      },
      containedPrimary: {
        background: 'linear-gradient(135deg, #007C5A 0%, #00634a 100%)',
        '&:hover': {
          background: 'linear-gradient(135deg, #008f68 0%, #007050 100%)',
        },
      },
      containedSecondary: {
        color: '#1a2e22',
      },
      outlined: {
        borderWidth: '1.5px',
        '&:hover': {
          borderWidth: '1.5px',
        },
      },
      outlinedPrimary: {
        '&:hover': {
          backgroundColor: 'rgba(0, 124, 90, 0.05)',
        },
      },
      text: {
        '&:hover': {
          backgroundColor: 'rgba(0, 124, 90, 0.06)',
        },
      },
    },
    MuiCard: {
      root: {
        borderRadius: 12,
        boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
        border: '1px solid rgba(0,0,0,0.06)',
        overflow: 'hidden',
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
      },
    },
    MuiCardContent: {
      root: {
        padding: '20px',
        '&:last-child': {
          paddingBottom: '20px',
        },
      },
    },
    MuiCardHeader: {
      root: {
        padding: '20px 20px 0',
      },
      title: {
        fontSize: '1rem',
        fontWeight: 600,
        color: '#1a2e22',
      },
      subheader: {
        fontSize: '0.8125rem',
        color: '#4f6b5c',
      },
    },
    MuiPaper: {
      elevation1: {
        boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
      },
      elevation2: {
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      },
      elevation3: {
        boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
      },
      elevation4: {
        boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
      },
    },
    MuiChip: {
      root: {
        borderRadius: 20,
        fontWeight: 600,
        fontSize: '0.75rem',
        height: 26,
        fontFamily: "'Montserrat', 'Roboto', sans-serif",
      },
      colorPrimary: {
        backgroundColor: 'rgba(0, 124, 90, 0.12)',
        color: '#005f44',
      },
      outlinedPrimary: {
        borderColor: 'rgba(0, 124, 90, 0.4)',
        color: '#007C5A',
      },
    },
    MuiTableContainer: {
      root: {
        borderRadius: 12,
        border: '1px solid rgba(0,0,0,0.06)',
        overflow: 'hidden',
      },
    },
    MuiTableHead: {
      root: {
        backgroundColor: '#f4f8f5',
        '& .MuiTableCell-head': {
          fontWeight: 700,
          fontSize: '0.7rem',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: '#4f6b5c',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          padding: '11px 16px',
          whiteSpace: 'nowrap',
          backgroundColor: '#f4f8f5',
        },
      },
    },
    MuiTableBody: {
      root: {
        '& .MuiTableRow-root': {
          transition: 'background-color 0.15s ease',
        },
        '& .MuiTableRow-root:hover': {
          backgroundColor: '#f8faf9',
        },
        '& .MuiTableRow-root:last-child .MuiTableCell-root': {
          borderBottom: 'none',
        },
      },
    },
    MuiTableCell: {
      root: {
        borderBottom: '1px solid rgba(0,0,0,0.06)',
        padding: '11px 16px',
        fontSize: '0.875rem',
        fontFamily: "'Montserrat', 'Roboto', sans-serif",
      },
    },
    MuiOutlinedInput: {
      root: {
        borderRadius: 8,
        backgroundColor: '#ffffff',
        transition: 'box-shadow 0.2s ease',
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: '#007C5A',
        },
        '&.Mui-focused': {
          boxShadow: '0 0 0 3px rgba(0, 124, 90, 0.15)',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderWidth: '1.5px',
        },
      },
      notchedOutline: {
        borderColor: 'rgba(0,0,0,0.15)',
        transition: 'border-color 0.2s ease',
      },
      input: {
        padding: '11px 14px',
        fontSize: '0.875rem',
      },
    },
    MuiInputLabel: {
      root: {
        fontSize: '0.875rem',
        fontWeight: 500,
        color: '#4f6b5c',
      },
    },
    MuiFormHelperText: {
      root: {
        fontSize: '0.75rem',
      },
    },
    MuiSelect: {
      select: {
        '&:focus': {
          borderRadius: 8,
          backgroundColor: 'transparent',
        },
      },
    },
    MuiDialog: {
      paper: {
        borderRadius: 16,
        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.22)',
      },
    },
    MuiDialogTitle: {
      root: {
        padding: '24px 24px 12px',
        '& .MuiTypography-root': {
          fontWeight: 700,
          fontSize: '1.1rem',
        },
      },
    },
    MuiDialogContent: {
      root: {
        padding: '12px 24px',
      },
      dividers: {
        borderColor: 'rgba(0,0,0,0.08)',
        padding: '16px 24px',
      },
    },
    MuiDialogActions: {
      root: {
        padding: '16px 24px 20px',
        gap: '8px',
      },
    },
    MuiTab: {
      root: {
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '0.875rem',
        letterSpacing: '0',
        minWidth: 'auto',
        padding: '10px 20px',
        transition: 'color 0.15s ease',
      },
      selected: {
        fontWeight: 700,
      },
    },
    MuiTabs: {
      indicator: {
        height: 3,
        borderRadius: '3px 3px 0 0',
        backgroundColor: '#007C5A',
      },
    },
    MuiTooltip: {
      tooltip: {
        borderRadius: 8,
        fontSize: '0.775rem',
        fontWeight: 500,
        backgroundColor: 'rgba(13, 40, 24, 0.92)',
        backdropFilter: 'blur(4px)',
        padding: '6px 12px',
      },
      arrow: {
        color: 'rgba(13, 40, 24, 0.92)',
      },
    },
    MuiIconButton: {
      root: {
        borderRadius: 8,
        padding: 8,
        transition: 'all 0.15s ease',
      },
    },
    MuiMenu: {
      paper: {
        borderRadius: 12,
        boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
        border: '1px solid rgba(0,0,0,0.06)',
        marginTop: 4,
      },
      list: {
        padding: '6px',
      },
    },
    MuiMenuItem: {
      root: {
        fontSize: '0.875rem',
        fontWeight: 500,
        padding: '8px 12px',
        borderRadius: 6,
        minHeight: 'auto',
        transition: 'background-color 0.15s ease',
        '&:hover': {
          backgroundColor: 'rgba(0, 124, 90, 0.07)',
        },
        '&.Mui-selected': {
          backgroundColor: 'rgba(0, 124, 90, 0.1)',
          '&:hover': {
            backgroundColor: 'rgba(0, 124, 90, 0.14)',
          },
        },
      },
    },
    MuiListItemIcon: {
      root: {
        minWidth: 36,
      },
    },
    MuiListItemText: {
      primary: {
        fontWeight: 500,
        fontSize: '0.875rem',
      },
    },
    MuiDivider: {
      root: {
        backgroundColor: 'rgba(0,0,0,0.07)',
      },
    },
    MuiCircularProgress: {
      colorPrimary: {
        color: '#007C5A',
      },
    },
    MuiLinearProgress: {
      root: {
        borderRadius: 4,
        height: 6,
        backgroundColor: 'rgba(0, 124, 90, 0.12)',
      },
      bar: {
        borderRadius: 4,
      },
      barColorPrimary: {
        background: 'linear-gradient(90deg, #007C5A, #39ac7d)',
      },
    },
    MuiAccordion: {
      root: {
        borderRadius: '12px !important',
        border: '1px solid rgba(0,0,0,0.07)',
        boxShadow: 'none',
        '&:before': {
          display: 'none',
        },
        marginBottom: 8,
        '&.Mui-expanded': {
          marginBottom: 8,
        },
      },
    },
    MuiAccordionSummary: {
      root: {
        borderRadius: 12,
        minHeight: 52,
        '&.Mui-expanded': {
          minHeight: 52,
        },
        padding: '0 16px',
      },
      content: {
        fontWeight: 600,
        '&.Mui-expanded': {
          margin: '12px 0',
        },
      },
    },
    MuiAccordionDetails: {
      root: {
        padding: '4px 16px 16px',
      },
    },
    MuiBadge: {
      badge: {
        fontWeight: 700,
        fontSize: '0.65rem',
        height: 18,
        minWidth: 18,
        borderRadius: 9,
      },
    },
    MuiAlert: {
      root: {
        borderRadius: 10,
        fontSize: '0.875rem',
        fontWeight: 500,
        alignItems: 'center',
      },
      standardSuccess: {
        backgroundColor: '#dcf5eb',
        color: '#0d4a2a',
        '& .MuiAlert-icon': { color: '#007C5A' },
      },
      standardError: {
        backgroundColor: '#fde8e8',
        color: '#7a1515',
        '& .MuiAlert-icon': { color: '#e53935' },
      },
      standardWarning: {
        backgroundColor: '#fef3cd',
        color: '#7a4a00',
        '& .MuiAlert-icon': { color: '#f59e0b' },
      },
      standardInfo: {
        backgroundColor: '#dbeafe',
        color: '#1e3a8a',
        '& .MuiAlert-icon': { color: '#3b82f6' },
      },
    },
    MuiSnackbarContent: {
      root: {
        borderRadius: 10,
      },
    },
  },
  mixins: {
    chip: {
      borderRadius: '20px',
      backgroundColor: 'rgba(0, 124, 90, 0.1)',
      color: '#007C5A',
      fontWeight: 600,
      fontSize: '0.75rem',
    },
  },
});

export default theme;
